import React, { useEffect, useRef, useState } from 'react';
import { Check, Heart, LayoutGrid, LayoutList, Loader2, MoreHorizontal } from 'lucide-react';
import { pageWindow } from './gallery';

export interface ReHubFilterOption {
  id: string;
  label: string;
  count?: number;
}

export function ReHubFilterBar({
  filters,
  activeId,
  onChange,
  sort,
  onSort,
  range,
  onRange,
  view,
  onView,
}: {
  filters: ReHubFilterOption[];
  activeId: string;
  onChange: (id: string) => void;
  sort: 'newest' | 'oldest';
  onSort: (sort: 'newest' | 'oldest') => void;
  range: 'all' | '7d' | '30d' | '90d';
  onRange: (range: 'all' | '7d' | '30d' | '90d') => void;
  view: 'grid' | 'list';
  onView: (view: 'grid' | 'list') => void;
}) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Filter photos">
        {filters.map((filter) => {
          const active = filter.id === activeId;
          return (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(filter.id)}
              className={`shrink-0 rounded-xl px-3 py-1.5 text-sm font-medium ${active ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}
            >
              {filter.label}
              {typeof filter.count === 'number' ? ` (${filter.count})` : ''}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex rounded-xl border border-white/10 p-0.5">
          <button type="button" aria-pressed={view === 'grid'} aria-label="Grid view" className={`rounded-lg p-1.5 ${view === 'grid' ? 'bg-orange-500 text-white' : 'text-slate-400'}`} onClick={() => onView('grid')}>
            <LayoutGrid size={16} />
          </button>
          <button type="button" aria-pressed={view === 'list'} aria-label="List view" className={`rounded-lg p-1.5 ${view === 'list' ? 'bg-orange-500 text-white' : 'text-slate-400'}`} onClick={() => onView('list')}>
            <LayoutList size={16} />
          </button>
        </div>
        <label className="sr-only" htmlFor="rehub-range">Date range</label>
        <select id="rehub-range" value={range} onChange={(event) => onRange(event.target.value as 'all' | '7d' | '30d' | '90d')} className="rounded-xl border border-white/10 bg-[#101622] px-2.5 py-1.5 text-sm text-slate-200">
          <option value="all">All time</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
        <label className="sr-only" htmlFor="rehub-sort">Sort</label>
        <select id="rehub-sort" value={sort} onChange={(event) => onSort(event.target.value as 'newest' | 'oldest')} className="rounded-xl border border-white/10 bg-[#101622] px-2.5 py-1.5 text-sm text-slate-200">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>
    </div>
  );
}

export interface ReHubCardMenuItem {
  id: string;
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export function ReHubImageCard({
  src,
  alt,
  title,
  meta,
  badge,
  badgeTone = 'accent',
  selected = false,
  onSelect,
  favourite = false,
  onToggleFavourite,
  menu,
  onOpen,
  busy = false,
  layout = 'grid',
}: {
  src?: string | null;
  alt: string;
  title: string;
  meta: string;
  badge?: string;
  badgeTone?: 'accent' | 'neutral' | 'danger' | 'warning';
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  favourite?: boolean;
  onToggleFavourite?: () => void;
  menu?: ReHubCardMenuItem[];
  onOpen?: () => void;
  busy?: boolean;
  layout?: 'grid' | 'list';
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const badgeClass = {
    accent: 'bg-orange-500/15 text-orange-200',
    neutral: 'bg-white/10 text-slate-200',
    danger: 'bg-red-500/15 text-red-200',
    warning: 'bg-amber-500/15 text-amber-200',
  }[badgeTone];

  const image = (
    <div className={`relative overflow-hidden bg-black ${layout === 'list' ? 'h-24 w-36 shrink-0 rounded-xl' : 'aspect-[4/3]'}`}>
      {onOpen ? (
        <button type="button" onClick={onOpen} className="absolute inset-0">
          {src ? <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-xs text-slate-500">No preview</span>}
        </button>
      ) : src ? (
        <img src={src} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-slate-500">No preview</div>
      )}
      {busy && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50">
          <Loader2 className="animate-spin text-orange-400" size={22} />
        </div>
      )}
      {onSelect && (
        <label className={`absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-md border ${selected ? 'border-orange-400 bg-orange-500 text-white' : 'border-white/40 bg-black/55 text-transparent'}`}>
          <input type="checkbox" className="sr-only" checked={selected} onChange={(event) => onSelect(event.target.checked)} aria-label={`Select ${title}`} />
          <Check size={14} />
        </label>
      )}
      {onToggleFavourite && (
        <button type="button" aria-pressed={favourite} aria-label={favourite ? `Remove ${title} from favourites` : `Add ${title} to favourites`} onClick={onToggleFavourite} className="absolute bottom-2 right-2 z-10 rounded-full bg-black/45 p-1.5 text-white hover:bg-black/70">
          <Heart size={14} className={favourite ? 'fill-rose-400 text-rose-400' : ''} />
        </button>
      )}
    </div>
  );

  return (
    <article className={`group relative rounded-2xl border border-white/10 bg-[#101622] ${layout === 'list' ? 'flex items-center gap-3 p-2' : 'overflow-visible'}`}>
      {image}
      <div className={`min-w-0 ${layout === 'list' ? 'flex flex-1 items-center gap-3 pr-2' : 'px-3 py-2.5'}`}>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-medium">{title}</h3>
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
            <span>{meta}</span>
            {badge && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${badgeClass}`}>{badge}</span>}
          </p>
        </div>
        {menu && menu.length > 0 && (
          <div className={`z-10 ${layout === 'grid' ? 'absolute right-2 top-2' : 'relative'}`} ref={menuRef}>
            <button type="button" aria-label={`Actions for ${title}`} aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)} className="rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70">
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 z-20 mt-1 w-48 rounded-xl border border-white/10 bg-[#161d2c] p-1 shadow-2xl">
                {menu.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => { setMenuOpen(false); item.onSelect(); }}
                    className={`block w-full rounded-lg px-3 py-2 text-left text-sm disabled:opacity-40 ${item.destructive ? 'text-red-300 hover:bg-red-500/10' : 'text-slate-200 hover:bg-white/5'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export function ReHubGallery({
  page,
  pageSize,
  total,
  onPageChange,
  view,
  children,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  view: 'grid' | 'list';
  children: React.ReactNode;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = total === 0 ? 0 : safePage * pageSize + 1;
  const end = Math.min(total, (safePage + 1) * pageSize);
  const pages = pageWindow(safePage, totalPages);
  return (
    <div className="space-y-4">
      <div className={view === 'grid' ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'flex flex-col gap-2'}>
        {children}
      </div>
      <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
        <p className="text-xs text-slate-400">{total === 0 ? 'No images' : `Showing ${start}–${end} of ${total}`}</p>
        {totalPages > 1 && (
          <nav className="flex items-center gap-1" aria-label="Gallery pages">
            {pages.map((entry, index) => entry === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-1 text-slate-500">…</span>
            ) : (
              <button
                key={entry}
                type="button"
                aria-current={entry === safePage ? 'page' : undefined}
                onClick={() => onPageChange(entry)}
                className={`h-8 min-w-8 rounded-lg px-2 text-sm ${entry === safePage ? 'bg-orange-500 text-white' : 'text-slate-300 hover:bg-white/5'}`}
              >
                {entry + 1}
              </button>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}

export function ReHubBatchActions({
  count,
  onClear,
  actions,
}: {
  count: number;
  onClear: () => void;
  actions: { id: string; label: string; onClick: () => void; destructive?: boolean; disabled?: boolean }[];
}) {
  if (count === 0) return null;
  return (
    <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-[#161d2c]/95 px-3 py-2 shadow-2xl backdrop-blur" role="toolbar" aria-label="Selected photos">
      <span className="px-1 text-sm font-medium">{count} selected</span>
      <button type="button" className="text-xs text-slate-400 underline-offset-2 hover:underline" onClick={onClear}>Clear</button>
      <span className="mx-1 hidden h-4 w-px bg-white/10 sm:block" />
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          disabled={action.disabled}
          onClick={action.onClick}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-40 ${action.destructive ? 'bg-red-500/15 text-red-200 hover:bg-red-500/25' : 'bg-white/10 text-white hover:bg-white/15'}`}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}
